import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
import { storageKeys } from "../../constants/storageKeys";
import { JobDatabaseListingPayloadType } from "../../Types/jobDatabaseTypes";

interface JobDatabaseFiltersContextType {
  jobDatabaseFilters: JobDatabaseListingPayloadType;
  updateJobDatabaseFilters: (
    params: Partial<JobDatabaseListingPayloadType>,
  ) => void;
  resetJobDatabaseFilters: () => void;
}

const JobDatabaseFiltersContext = createContext<
  JobDatabaseFiltersContextType | undefined
>(undefined);

export const useJobDatabaseContext = (): JobDatabaseFiltersContextType => {
  const context = useContext(JobDatabaseFiltersContext);
  if (!context) {
    throw new Error(
      "useJobDatabaseContext must be used within a JobDatabaseFilterContextProvider",
    );
  }
  return context;
};

const initialFilters: JobDatabaseListingPayloadType = {
  search: undefined,
  isBlocked: false,
  jobNumber: [],
  jobName: [],
  companyId: [],
  jobType: [],
  equipment: [],
  materialCategory: [],
  materialType: [],
  salesPersonId: [],
  sortBy: undefined,
  sortOrder: undefined,
};

export const JobDatabaseFilterContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const [jobDatabaseFilters, setJobDatabaseFilters] =
    useState<JobDatabaseListingPayloadType>(initialFilters);

  const updateJobDatabaseFilters = useCallback(
    (params: JobDatabaseListingPayloadType) => {
      setJobDatabaseFilters((prevState) => {
        const nextFilters = { ...prevState, ...params, page: params?.page };
        sessionStorage.setItem(
          storageKeys.JOB_DB_FILTERS,
          JSON.stringify(nextFilters),
        );
        return nextFilters;
      });
    },
    [],
  );

  const resetJobDatabaseFilters = () => {
    setJobDatabaseFilters(initialFilters);
    sessionStorage.removeItem(storageKeys.JOB_DB_FILTERS);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(
        storageKeys.JOB_DB_FILTERS,
        JSON.stringify(jobDatabaseFilters),
      );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (
      location &&
      !location?.pathname?.startsWith(appRoutes.JOB_DATABASE) &&
      !location?.pathname?.startsWith(appRoutes.JOB_PROFILE)
    ) {
      resetJobDatabaseFilters();
      sessionStorage.removeItem(storageKeys.JOB_DB_PAGE);
    }
  }, [window.location.pathname]);

  return (
    <JobDatabaseFiltersContext.Provider
      value={{
        jobDatabaseFilters,
        updateJobDatabaseFilters,
        resetJobDatabaseFilters,
      }}
    >
      {children}
    </JobDatabaseFiltersContext.Provider>
  );
};
